import axios, { AxiosRequestConfig } from 'axios';
import { store } from 'src/redux/Store';
import fileDownload from 'js-file-download';

import { headers } from 'src/constants/Endpoints';
import { setAuthData } from 'src/redux/actions/userActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { API_URL } from '../environments/environment';

export enum DownloadOption {
  download = 'download',
  display = 'display',
  tab = 'tab',
}

export interface RequestOptions {
  url: string,
  external?: boolean,
  method: string
  data?: object | string
  headers?: object
  organisation: string
  download?: boolean;
}

export default function (
  options: RequestOptions,
  downloadOption: DownloadOption = DownloadOption.download,
  filename?: string,
): Promise<APIResponse> {
  const headersAIC = {
    'x-aic-organisation': options.organisation || 'aic',
    Authorization: '',
    'X-AIC-LOCALE': store.getState().language.locale,
  };
  const { accessToken } = store.getState().auth;
  if (accessToken) {
    headersAIC.Authorization = `Bearer ${accessToken}`;
  }
  let url = options.url ? API_URL + options.url : API_URL;
  if (options.external) {
    url = options.url;
  }

  const config = {
    data: options.data,
    headers: options.external
      ? undefined
      : {
        ...headers.DEFAULT,
        ...options.headers,
        ...headersAIC,
      },
    method: options.method,
    url,
  } as AxiosRequestConfig;

  if (filename) {
    config.responseType = 'blob';
  }

  return axios(config)
    .then(async (response) => {
      if (options.external) {
        return {
          success: true,
          status: response.status,
        } as APIResponse;
      }
      if (filename) {
        if (response.status !== 200) {
          const text = await response.data.text();
          const json = JSON.parse(text);
          return {
            data: json.data ?? undefined,
            success: json.success,
            status: response.status,
          };
        }
        if (response.status === 200) {
          if (downloadOption === DownloadOption.tab) {
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
          } else {
            fileDownload(response.data, filename);
          }
        }
      }
      if (!('data' in response.data)) {
        return {
          data: response.data,
          success: response.data.success,
          status: response.status,
        };
      }
      if (response.status === 403) {
        if (accessToken) {
          store.dispatch(setAlert({
            type: ALERT_TYPE.warning,
            message: 'Session expired',
          }));
          store.dispatch(setAuthData({
            accessToken: null,
            login: false,
            remember: false,
          }));
        }
        return {
          data: undefined,
          success: false,
          status: response.status,
        };
      }
      return {
        data: response.data.data,
        success: response.data.success,
        status: response.status,
      };
    })
    .catch((response) => {
      if (!response.response || !response.response.data) {
        if (response.message.stack) {
          return {
            data: undefined,
            success: false,
            errors: true,
            status: response.status,
            message: response.message.stack,
          };
        }
        return {
          data: undefined,
          success: false,
          errors: true,
          status: response.status,
          message: response,
        };
      }
      const {
        code, errors, message, data, success,
      } = response.response.data;
      return {
        data,
        success,
        errors,
        status: code,
        message,
      };
    });
}

export const makeApiRequestOpenInTab = (
  options: RequestOptions,
): Promise<APIResponse> => {
  const headersAIC = {
    'x-aic-organisation': options.organisation || 'aic',
    Authorization: '',
    'X-AIC-LOCALE': store.getState().language.locale,
  };
  const { accessToken } = store.getState().auth;
  if (accessToken) {
    headersAIC.Authorization = `Bearer ${accessToken}`;
  }

  const config: AxiosRequestConfig = {
    data: options.data,
    headers: {
      ...headers.DEFAULT,
      ...options.headers,
      ...headersAIC,
    },
    method: options.method,
    url: API_URL + options.url,
    responseType: 'blob',
  };

  return axios(config)
    .then(async (response) => {
      if (response.status !== 200) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        return {
          data: json.data ?? undefined,
          success: json.success,
          status: response.status,
        };
      }
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = URL.createObjectURL(blob);
      const newTab = window.open(blobUrl, '_blank');
      if (newTab) {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      }
      return {
        data: undefined,
        success: true,
        status: response.status,
      };
    })
    .catch((response) => {
      if (!response.response || !response.response.data) {
        return {
          data: undefined,
          success: false,
          errors: true,
          status: response.status,
          message: response.message.stack ?? response,
        };
      }
      const {
        code, errors, message, data, success,
      } = response.response.data;
      return {
        data,
        success,
        errors,
        status: code,
        message,
      };
    });
};
